/**
 * ProvenancePopover — Phase 6.AY admin-only render-provenance surface.
 *
 * Sits as a small click target NEXT TO the existing `LiveDataChip` on the
 * Analyze page. Only owner/admin users see it. Click expands a compact
 * popover with the full provenance details for THIS rendered analysis:
 *
 *   - data_source         (live_fmp | partial_live_fmp | analysis_cache | …)
 *   - cache_hit
 *   - cache_layers_used
 *   - mock_used
 *   - fallback_used
 *   - endpoint_coverage_pct
 *   - endpoints_used / endpoints_failed / plan_restricted_endpoints
 *   - unavailable_fields (sparse-data fingerprint)
 *   - chart_data_status / chart_points_count
 *   - analysis_id   (proves this matches an exact compute event)
 *   - request_id
 *   - LIVE_DATA_ONLY contract status + violation flag
 *
 * Normal users NEVER see this. The trigger renders nothing unless the
 * caller component passed `visible=true` (caller decides admin-ness).
 *
 * Design intent (matches user spec):
 *   * keep the analyze page visually clean and premium for paid users
 *   * never add an always-visible inline debug row for now
 *   * keep the technical detail behind a click target
 */
import React, { useState } from "react";
import { Wrench, Copy, Check, AlertTriangle, ShieldCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

function Row({ label, value, mono = true, tone = null }) {
  const colors = {
    danger: "text-rose-600",
    warn: "text-amber-600",
    ok: "text-emerald-600",
    null: "text-muted-foreground/70 italic",
  };
  const display = value === null || value === undefined
    ? "null"
    : Array.isArray(value)
      ? (value.length === 0 ? "[]" : value.join(", "))
      : typeof value === "boolean"
        ? (value ? "true" : "false")
        : String(value);
  const colorClass = tone ? colors[tone] : (value === null || value === undefined ? colors.null : "");
  return (
    <div className="flex items-start justify-between gap-3 text-[11px] leading-tight">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={`${mono ? "font-mono" : ""} text-right break-all ${colorClass}`}>
        {display}
      </span>
    </div>
  );
}

export default function ProvenancePopover({ data, visible = false }) {
  const [copied, setCopied] = useState(false);
  if (!visible || !data) return null;
  const meta = data._meta_debug || {};
  const prov = meta.provenance || {};

  // Surface key flags
  const violation = !!prov.live_data_only_violation;
  const mockUsed = !!prov.mock_used;
  const zeroSub = !!prov.zero_substitution_detected;

  // Headline tone — green when fully live, amber when partial / cache, rose when violation
  let badgeTone = "neutral";
  let badgeLabel = prov.data_source || meta.data_source || "unknown";
  if (violation) {
    badgeTone = "danger";
    badgeLabel = "INVALID · mock in LIVE_DATA_ONLY";
  } else if (mockUsed) {
    badgeTone = "warn";
  } else if (prov.data_source === "live_fmp") {
    badgeTone = "ok";
  } else if (
    prov.data_source === "analysis_cache" ||
    prov.data_source === "partial_live_fmp" ||
    prov.data_source === "raw_fmp_cache" ||
    prov.data_source === "chart_cache"
  ) {
    badgeTone = "warn";
  }

  const triggerTone = violation
    ? "border-rose-500/50 bg-rose-500/10 text-rose-700"
    : mockUsed
      ? "border-amber-500/50 bg-amber-500/10 text-amber-700"
      : prov.data_source === "live_fmp"
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
        : "border-border/70 bg-muted/40 text-muted-foreground";

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(prov, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium hover:opacity-80 transition-opacity ${triggerTone}`}
          data-testid="provenance-popover-trigger"
          aria-label="Show data provenance (admin only)"
        >
          {violation
            ? <AlertTriangle className="w-3 h-3" aria-hidden="true" />
            : <Wrench className="w-3 h-3" aria-hidden="true" />}
          <span>debug</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[380px] max-h-[70vh] overflow-y-auto"
        data-testid="provenance-popover-content"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {violation
              ? <AlertTriangle className="w-4 h-4 text-rose-600" aria-hidden="true" />
              : <ShieldCheck className="w-4 h-4 text-emerald-600" aria-hidden="true" />}
            <span className="text-sm font-semibold">Render provenance</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={copyJson}
            data-testid="provenance-popover-copy-btn"
          >
            {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
            {copied ? "Copied" : "Copy JSON"}
          </Button>
        </div>

        {violation && (
          <div
            className="mb-3 rounded-md border border-rose-500/50 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-700"
            data-testid="provenance-popover-violation-banner"
          >
            <div className="font-semibold mb-0.5">LIVE_DATA_ONLY violation</div>
            <div>{prov.live_data_only_violation_reason || "Mock data used while LIVE_DATA_ONLY is active. This result is INVALID."}</div>
          </div>
        )}

        <section className="mb-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Identity</div>
          <div className="space-y-1">
            <Row label="ticker" value={prov.ticker || data.ticker} />
            <Row label="analysis_id" value={prov.analysis_id || data.analysis_id} />
            <Row label="request_id" value={prov.request_id || meta.request_id} />
            <Row label="generated_at" value={prov.generated_at} />
          </div>
        </section>

        <section className="mb-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Source</div>
          <div className="space-y-1">
            <Row label="data_source" value={badgeLabel} tone={badgeTone === "ok" ? "ok" : badgeTone === "danger" ? "danger" : badgeTone === "warn" ? "warn" : null} />
            <Row label="cache_hit" value={prov.cache_hit} tone={prov.cache_hit ? "warn" : null} />
            <Row label="cache_layers_used" value={prov.cache_layers_used} />
            <Row label="mock_used" value={prov.mock_used} tone={prov.mock_used ? "danger" : "ok"} />
            <Row label="fallback_used" value={prov.fallback_used} tone={prov.fallback_used ? "warn" : "ok"} />
            <Row label="recovered_from_cache" value={prov.recovered_from_cache} />
            <Row label="valuation_input_source" value={prov.valuation_input_source} />
            <Row label="ai_input_source" value={prov.ai_input_source} />
          </div>
        </section>

        <section className="mb-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Endpoint coverage</div>
          <div className="space-y-1">
            <Row label="endpoint_coverage_pct" value={prov.endpoint_coverage_pct === null ? null : `${prov.endpoint_coverage_pct}%`} tone={prov.endpoint_coverage_pct === 100 ? "ok" : prov.endpoint_coverage_pct === null ? null : "warn"} />
            <Row label="endpoints_used" value={prov.endpoints_used} />
            <Row label="plan_restricted" value={prov.plan_restricted_endpoints} tone={(prov.plan_restricted_endpoints || []).length ? "warn" : null} />
            <Row label="endpoints_failed" value={(prov.endpoints_failed || []).map((f) => f.path || f.key)} tone={(prov.endpoints_failed || []).length ? "warn" : null} />
          </div>
        </section>

        <section className="mb-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Chart</div>
          <div className="space-y-1">
            <Row label="chart_data_status" value={prov.chart_data_status} tone={prov.chart_data_status === "live_fmp" ? "ok" : "warn"} />
            <Row label="chart_endpoint_used" value={prov.chart_endpoint_used} />
            <Row label="chart_endpoint_last_reason" value={prov.chart_endpoint_last_reason} />
            <Row label="chart_endpoint_last_status" value={prov.chart_endpoint_last_status} />
          </div>
        </section>

        <section className="mb-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">KPI availability</div>
          <div className="space-y-1">
            <Row label="kpi_coverage_pct" value={prov.kpi_coverage_pct === null ? null : `${prov.kpi_coverage_pct}%`} />
            <Row label="unavailable_fields" value={prov.unavailable_fields} tone={(prov.unavailable_fields || []).length ? "warn" : null} />
          </div>
        </section>

        <section className="mb-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Contract</div>
          <div className="space-y-1">
            <Row label="live_data_only_mode" value={prov.live_data_only_mode} />
            <Row label="live_data_only_violation" value={prov.live_data_only_violation} tone={prov.live_data_only_violation ? "danger" : "ok"} />
            <Row label="null_values_preserved" value={prov.null_values_preserved} tone={prov.null_values_preserved ? "ok" : "danger"} />
            <Row label="zero_substitution_detected" value={zeroSub} tone={zeroSub ? "danger" : "ok"} />
          </div>
        </section>

        {/* Phase 6.BE — Canonical valuation + consistency-guard surface (admin-only). */}
        {(() => {
          const canonical = data?.canonical_valuation || {};
          const guard = (data?.valuation?.consistency_guard
                        || data?.valuation_consistency || {});
          const tracker = (data?.stage_tracker_snapshot
                          || meta?.stage_tracker
                          || meta?.stages
                          || {});
          return (
            <>
              <section className="mb-3" data-testid="provenance-popover-canonical-valuation">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Canonical valuation</div>
                <div className="space-y-1">
                  <Row label="current_price" value={canonical.current_price} />
                  <Row label="fair_value" value={canonical.fair_value} />
                  <Row label="fair_value_low" value={canonical.fair_value_low} />
                  <Row label="fair_value_high" value={canonical.fair_value_high} />
                  <Row label="upside_pct" value={canonical.upside_pct === null || canonical.upside_pct === undefined ? null : `${canonical.upside_pct}%`} />
                  <Row label="status" value={canonical.valuation_status} />
                  <Row label="confidence" value={canonical.confidence} />
                  <Row label="mode" value={canonical.mode} />
                  <Row label="opportunity_score" value={data?.opportunity_score ?? data?.score} />
                  <Row label="risk_level" value={data?.risk_level} />
                  <Row label="result_validity" value={data?.result_validity} />
                </div>
              </section>

              <section className="mb-3" data-testid="provenance-popover-consistency-guard">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Consistency guard</div>
                <div className="space-y-1">
                  <Row label="applied" value={guard.applied} tone={guard.applied ? "ok" : null} />
                  <Row label="valuation_consistency_suppressed" value={guard.valuation_consistency_suppressed} tone={guard.valuation_consistency_suppressed ? "warn" : "ok"} />
                  <Row label="suppression_count" value={guard.suppression_count} tone={(guard.suppression_count || 0) > 0 ? "warn" : null} />
                  <Row label="suppressed_risk_codes" value={guard.suppressed_risk_codes} tone={(guard.suppressed_risk_codes || []).length ? "warn" : null} />
                  <Row label="tolerance_pct_pts" value={guard.tolerance_pct_pts} />
                </div>
              </section>

              <section className="mb-3" data-testid="provenance-popover-stage-tracker">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Pipeline stages</div>
                <div className="space-y-1">
                  <Row label="last_completed_stage" value={data?.last_completed_stage ?? tracker?.last_completed_stage} />
                  <Row label="skipped_stages" value={data?.skipped_stages ?? tracker?.stages_skipped} />
                  <Row label="ai_summary_status" value={data?.ai_summary_status} />
                  <Row label="llm_skipped_reason" value={data?.llm_skipped_reason} />
                  <Row label="partial_response" value={data?.partial_response} tone={data?.partial_response ? "warn" : "ok"} />
                  <Row label="total_elapsed_ms" value={tracker?.total_elapsed_ms} />
                </div>
              </section>

              {/* Phase 6.BG — AI diagnostics (admin-only). Quick inspection
                  of which model was requested, whether the fallback fired,
                  and the precise error type if AI was skipped. */}
              {meta?.ai_diagnostics ? (
                <section className="mb-1" data-testid="provenance-popover-ai-diagnostics">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">AI diagnostics</div>
                  <div className="space-y-1">
                    <Row label="ai_model_requested" value={meta.ai_diagnostics.ai_model_requested} />
                    <Row label="ai_model_used" value={meta.ai_diagnostics.ai_model_used} tone={meta.ai_diagnostics.ai_model_used ? "ok" : "warn"} />
                    <Row label="ai_retry_attempted" value={meta.ai_diagnostics.ai_retry_attempted} />
                    <Row label="ai_retry_model" value={meta.ai_diagnostics.ai_retry_model} />
                    <Row label="ai_duration_ms" value={meta.ai_diagnostics.ai_duration_ms} />
                    <Row label="ai_error_type" value={meta.ai_diagnostics.ai_error_type} tone={meta.ai_diagnostics.ai_error_type && meta.ai_diagnostics.ai_error_type !== "not_attempted" ? "warn" : null} />
                    <Row label="ai_skip_reason" value={meta.ai_diagnostics.ai_skip_reason} />
                    <Row label="llm_provider_status" value={meta.ai_diagnostics.llm_provider_status} />
                    <Row label="llm_budget_status" value={meta.ai_diagnostics.llm_budget_status} />
                    {Array.isArray(meta.ai_diagnostics.attempts) && meta.ai_diagnostics.attempts.length > 0 ? (
                      <div className="pt-1">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Attempts</div>
                        <ul className="space-y-0.5 text-[10.5px] font-mono break-all">
                          {meta.ai_diagnostics.attempts.map((a, i) => (
                            <li key={i}>
                              #{i + 1} {a.model} · {a.ms}ms · {a.outcome}{a.error_type ? ` (${a.error_type})` : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </section>
              ) : null}
            </>
          );
        })()}
      </PopoverContent>
    </Popover>
  );
}
